import React, { useEffect, useRef, useState } from 'react'
import { Title } from '@/components/quiz/Title'
import { Description } from '@/components/quiz/Description'
import { BeforeAfter } from '@/components/quiz/paywall/BeforeAfter'
import { Button } from '@/components/Button/Button'
import { Features } from '@/components/quiz/paywall/Features'
import { SpecialOffer } from '@/components/quiz/paywall/SpecialOffer'
import { Plans } from '@/components/quiz/paywall/Plans'
import { CardsSwiper } from '@/components/quiz/paywall/CardsSwiper'
import { UserCounter } from '@/components/quiz/paywall/UserCounter'
import { Reviews } from '@/components/quiz/Reviews'
import { RiskFree } from '@/components/quiz/paywall/RiskFree'
import { useQuizStore } from '@/store/useQuizStore'
import { checkUserSubscription } from '@/helpers/appHelpers'
import { PopupStatus } from '@/components//quiz/paywall/PopupStatus'
import { useNavigate } from 'react-router-dom'

export const Paywall = ({ data, onNextStep }: { data: any; onNextStep: () => void }) => {
  const [activePlan, setActivePlan] = useState(parseInt(localStorage.activePlanIndex ?? '0'))
  const setSingle = useQuizStore((s) => s.setSingle)
  const [userHasSubscription, setUserHasSubscription] = useState(false)
  const email = localStorage.getItem('userEmail') ?? ''
  const navigate = useNavigate()
  const planRef = useRef<HTMLDivElement>(null)

  const scrollTo = () => {
    planRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    checkUserSubscription(email ?? '').then((res) => {
      setUserHasSubscription(res.user && res.user.subscriptions.length > 0)
    })
  }, [])

  const handlePlan = (index: number) => {
    setActivePlan(index)
    localStorage.setItem('activePlanIndex', index.toString())
  }

  useEffect(() => {
    localStorage.setItem('activePlan', JSON.stringify(data.items[activePlan]))
  }, [activePlan])

  const handleCheckout = () => {
    setSingle('plan', JSON.stringify(data.items[activePlan]))
    onNextStep()
  }

  return (
    <>
      <Title data={{ title: 'Everything cleaned. Everything under control.', isSmall: true }} />
      <Description
        data={{
          description: 'Faster, cleaner, quieter — exactly <br> how your phone should feel.',
        }}
      />
      <BeforeAfter />

      <div className="mt-8"></div>
      <Description
        data={{
          description: 'See your phone go from cluttered <br> to clean in just one scan.',
          bold: true,
        }}
      />

      <Button onClick={scrollTo} className="mt-8 pulsating-button" variant="multistep">
        Get my plan
      </Button>

      <div className="mt-[128px]"></div>

      <Title data={{ title: 'Refresh. Restore. <br> Reimagine your phone.', isSmall: true }} />

      <Features />

      <div ref={planRef}></div>
      <SpecialOffer percentDiscount={data.items[activePlan].percentDiscount} />

      <Plans
        onClick={handleCheckout}
        onSelectPlan={handlePlan}
        data={{ items: data.items, active: activePlan }}
      />
      <div className="mt-[128px]"></div>

      <Title data={{ title: 'Get your phone back <br> to perfect today', isSmall: true }} />

      <CardsSwiper
        data={{
          items: [
            {
              icon: '/quiz/icons/performance-icon.png',
              title: 'Boost phone performance',
              description: 'Clear hidden junk to speed up your device',
            },
            {
              icon: '/quiz/icons/duplicate.png',
              title: 'Duplicate photo & video remover',
              description: 'Free up valuable space with a single tap',
            },
            {
              icon: '/quiz/icons/battery.png',
              title: 'Save battery power efficiently',
              description: 'Stop background apps from draining power',
            },
            {
              icon: '/quiz/icons/compress.png',
              title: 'Compress large videos',
              description: 'Keep memories while freeing up storage',
            },
            {
              icon: '/quiz/icons/spam3.png',
              title: 'Clean your mail from spam',
              description: 'Block unwanted emails and declutter inbox',
            },
            {
              icon: '/quiz/icons/phone.png',
              title: 'Organize & clean contacts',
              description: 'Merge duplicates and tidy your address book',
            },
            {
              icon: '/quiz/icons/gallery.png',
              title: 'Swipe to clean your gallery',
              description: 'Quickly remove unwanted photos',
            },
            {
              icon: '/quiz/icons/app.png',
              title: 'Charging widgets & animations',
              description: 'Personalize your phone with smart visuals',
            },
            {
              icon: '/quiz/icons/folder.png',
              title: 'Secure files with secret folder',
              description: 'Keep sensitive files secure and private',
            },
            {
              icon: '/quiz/icons/clean2.png',
              title: 'Enjoy a smooth, intuitive interface',
              description: 'Beautifully simple, designed for everyone',
            },
          ],
          className: '!mt-8',
        }}
      />
      <div className="mt-[70px]"></div>

      <UserCounter />

      <div className="mt-[128px]"></div>

      <Title data={{ title: 'Real people. Real results.', isSmall: true }} />

      <Reviews
        data={{
          className: '!mt-8',
          items: [
            {
              name: 'Floyd Miles',
              avatar: '/quiz/images/author1.png',
              title: 'Battery drain',
              review:
                '“My phone battery used to die in just a few hours. After cleaning unused apps, the battery lasts much longer.”',
            },
            {
              name: 'Cameron Williamson',
              avatar: '/quiz/images/author2.png',
              title: 'Spam & pop-ups',
              review:
                '“I was very annoyed by spam messages and ads. Clean Master removed all that junk, now my phone feels peaceful.”',
            },
            {
              name: 'Brooklyn Simmons',
              avatar: '/quiz/images/author3.png',
              title: 'Slow phone',
              review:
                '“My phone was always freezing, I almost bought a new one. With Clean Master, it works fast again, just like new.”',
            },
          ],
        }}
      />

      <Title
        data={{ title: 'Fix it all — speed, storage, <br> and spam in one tap', isSmall: true }}
      />

      <Features />

      <SpecialOffer percentDiscount={data.items[activePlan].percentDiscount} />

      <Plans
        onClick={handleCheckout}
        onSelectPlan={handlePlan}
        data={{ items: data.items, active: activePlan }}
      />

      <div className="mt-[128px]"></div>

      <RiskFree />

      {userHasSubscription && (
        <PopupStatus
          icon="/img/success-icon.png"
          title="You already have subscription"
          buttonTitle="Proceed To Download"
          onClick={() => navigate('/quiz/thank-you')}
        />
      )}
    </>
  )
}
